/**
 * The page side of AVFoundation's capture sessions (docs/ops-protocol.md,
 * "Code scanning"). Where the browser has `BarcodeDetector` and a camera
 * (Chrome on Android and macOS, today), `scan` opens the camera full screen
 * over the page and answers with the first code it reads and the frame it
 * read it in. Elsewhere, or when there is no camera, it asks the viewer to
 * type or paste the code; a refused camera fails.
 */

import type { ScannerRequestParams } from './protocol';

/** A code the camera read (corners in video pixels), or one the viewer typed (no corners). */
export interface ScannedCode {
  string: string;
  format: string;
  corners: { x: number; y: number }[];
  /** The camera frame the code was read in, a base64 JPEG. */
  image?: string;
}

export type ScannerResult = { camera: boolean } | ScannedCode | { cancelled: true };

export interface ScanOptions {
  torch: boolean;
  vibrate: boolean;
  facing: 'environment' | 'user';
}

/** The browser's camera and barcode detection; tests substitute them. */
export interface ScannerHost {
  /** Whether there is a camera and a detector for at least one of `formats`. */
  available(formats: readonly string[]): Promise<boolean>;
  /**
   * Shows the camera (or asks for a typed code) until a code of `formats` is
   * found (resolves with it) or the viewer closes it (null). Rejects with
   * `permission: …` when the camera is refused.
   */
  scan(formats: readonly string[], options: ScanOptions): Promise<ScannedCode | null>;
}

export async function performScanner(params: ScannerRequestParams, host: ScannerHost | undefined): Promise<ScannerResult> {
  switch (params.action) {
    case 'probe':
      return { camera: (await host?.available(params.formats).catch(() => false)) ?? false };
    case 'scan': {
      if (!host) throw new Error('unsupported: this page cannot scan codes');
      const options: ScanOptions = { torch: params.torch ?? false, vibrate: params.vibrate ?? false, facing: params.facing ?? 'environment' };
      const code = await host.scan(params.formats, options);
      return code ?? { cancelled: true };
    }
    default:
      throw new Error(`unsupported: scanner action ${String((params as { action: unknown }).action)}`);
  }
}

interface DetectedBarcode {
  rawValue: string;
  format: string;
  cornerPoints: { x: number; y: number }[];
}

interface BarcodeDetectorLike {
  detect(source: HTMLVideoElement): Promise<DetectedBarcode[]>;
}

interface BarcodeDetectorClass {
  new (options: { formats: string[] }): BarcodeDetectorLike;
  getSupportedFormats(): Promise<string[]>;
}

/** The camera with `BarcodeDetector` where the browser has both, else a typed code; nothing outside a browser. */
export function browserScannerHost(): ScannerHost | undefined {
  if (typeof window === 'undefined' || typeof document === 'undefined') return undefined;
  const Detector = (window as unknown as { BarcodeDetector?: BarcodeDetectorClass }).BarcodeDetector;
  const canCapture = typeof navigator.mediaDevices?.getUserMedia === 'function';
  const supported = async (formats: readonly string[]): Promise<string[]> => {
    if (!Detector) return [];
    const known = await Detector.getSupportedFormats();
    return formats.filter((format) => known.includes(format));
  };
  return {
    available: async (formats) => {
      if (!canCapture || (await supported(formats)).length === 0) return false;
      const devices = await navigator.mediaDevices.enumerateDevices();
      return devices.some((device) => device.kind === 'videoinput');
    },
    scan: async (formats, options) => {
      const readable = await supported(formats);
      if (!Detector || !canCapture || readable.length === 0) return typedCode(formats);
      let stream: MediaStream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: options.facing }, audio: false });
      } catch (error) {
        const name = error instanceof Error ? error.name : '';
        const message = error instanceof Error ? error.message : String(error);
        if (name === 'NotAllowedError' || name === 'SecurityError') throw new Error(`permission: ${message}`);
        // No camera after all (or one that would not start): the viewer types the code.
        return typedCode(formats);
      }
      const detector = new Detector({ formats: readable });
      if (options.torch) {
        const [track] = stream.getVideoTracks();
        await track?.applyConstraints({ advanced: [{ torch: true } as MediaTrackConstraintSet] }).catch(() => undefined);
      }
      return cameraOverlay(stream, detector, options.vibrate);
    },
  };
}

const OVERLAY_STYLE = 'position:fixed;inset:0;z-index:2147483647;display:flex;align-items:center;justify-content:center';
const FONT = '-apple-system,system-ui,sans-serif';

function pillButton(label: string, primary: boolean): HTMLButtonElement {
  const button = document.createElement('button');
  button.type = 'button';
  button.textContent = label;
  button.style.cssText = `padding:12px 28px;border:0;border-radius:999px;font:600 17px ${FONT};color:#fff;background:${
    primary ? '#0088ff' : 'rgba(255,255,255,.25)'
  };backdrop-filter:blur(12px)`;
  return button;
}

/** The video's current frame as a base64 JPEG (no prefix); undefined when it cannot be read. */
function frameJPEG(video: HTMLVideoElement): string | undefined {
  try {
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    if (canvas.width === 0 || canvas.height === 0) return undefined;
    canvas.getContext('2d')?.drawImage(video, 0, 0);
    return canvas.toDataURL('image/jpeg', 0.85).split(',')[1];
  } catch {
    return undefined;
  }
}

/** The camera full screen with a viewfinder and a Cancel button, polling the detector. */
function cameraOverlay(stream: MediaStream, detector: BarcodeDetectorLike, vibrate: boolean): Promise<ScannedCode | null> {
  const overlay = document.createElement('div');
  overlay.dataset['sbScanner'] = 'camera';
  overlay.style.cssText = `${OVERLAY_STYLE};background:#000`;
  const video = document.createElement('video');
  video.muted = true;
  video.playsInline = true;
  video.autoplay = true;
  video.srcObject = stream;
  video.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;object-fit:cover';
  const finder = document.createElement('div');
  finder.style.cssText = 'position:relative;width:min(70vw,70vh);aspect-ratio:1;border:3px solid rgba(255,255,255,.9);border-radius:24px;box-shadow:0 0 0 100vmax rgba(0,0,0,.35)';
  const cancel = pillButton('Cancel', false);
  cancel.style.cssText += ';position:absolute;bottom:calc(env(safe-area-inset-bottom) + 32px);left:50%;transform:translateX(-50%)';
  overlay.append(video, finder, cancel);
  document.body.append(overlay);
  void video.play().catch(() => undefined);

  return new Promise((resolve) => {
    let done = false;
    const finish = (code: ScannedCode | null): void => {
      if (done) return;
      done = true;
      for (const track of stream.getTracks()) track.stop();
      overlay.remove();
      resolve(code);
    };
    cancel.addEventListener('click', () => finish(null));
    const tick = async (): Promise<void> => {
      if (done) return;
      if (video.readyState >= 2) {
        try {
          const [found] = await detector.detect(video);
          if (found) {
            if (vibrate) navigator.vibrate?.(100);
            const image = frameJPEG(video);
            finish({
              string: found.rawValue,
              format: found.format,
              corners: found.cornerPoints.map(({ x, y }) => ({ x, y })),
              ...(image ? { image } : {}),
            });
            return;
          }
        } catch {
          // A frame the detector could not read; try the next one.
        }
      }
      setTimeout(() => void tick(), 120);
    };
    void tick();
  });
}

/** Where the browser cannot read codes: a dialog over the page asking for the code's contents. */
export function typedCode(formats: readonly string[]): Promise<ScannedCode | null> {
  const overlay = document.createElement('div');
  overlay.dataset['sbScanner'] = 'typed';
  overlay.style.cssText = `${OVERLAY_STYLE};background:rgba(0,0,0,.6);padding:16px`;
  const panel = document.createElement('form');
  panel.style.cssText = `width:min(420px,100%);display:flex;flex-direction:column;gap:12px;padding:20px;border-radius:20px;background:#1c1c1e;color:#fff;font:15px ${FONT}`;
  const title = document.createElement('h2');
  title.textContent = 'Enter a Code';
  title.style.cssText = 'margin:0;font-size:20px;font-weight:600';
  const note = document.createElement('p');
  note.textContent = "This browser can't read codes with the camera. Type or paste what the code holds.";
  note.style.cssText = 'margin:0;color:rgba(255,255,255,.7)';
  const field = document.createElement('textarea');
  field.rows = 3;
  field.setAttribute('aria-label', 'Code contents');
  field.style.cssText = `resize:vertical;padding:10px;border-radius:10px;border:1px solid rgba(255,255,255,.2);background:#2c2c2e;color:#fff;font:15px ${FONT}`;
  const buttons = document.createElement('div');
  buttons.style.cssText = 'display:flex;gap:12px;justify-content:flex-end';
  const cancel = pillButton('Cancel', false);
  const done = pillButton('Done', true);
  done.type = 'submit';
  buttons.append(cancel, done);
  panel.append(title, note, field, buttons);
  overlay.append(panel);
  document.body.append(overlay);
  field.focus();

  return new Promise((resolve) => {
    const finish = (code: ScannedCode | null): void => {
      overlay.remove();
      resolve(code);
    };
    cancel.addEventListener('click', () => finish(null));
    panel.addEventListener('submit', (event) => {
      event.preventDefault();
      // Line breaks are kept: a QR code holds several lines as often as one.
      const string = field.value.replace(/\r\n/g, '\n');
      if (string.length === 0) return;
      finish({ string, format: formats[0] ?? 'qr_code', corners: [] });
    });
  });
}
